import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Box, 
  CircularProgress, 
  Typography, 
  Alert, 
  Paper,
  Button
} from '@mui/material';
import { CheckCircle, Error } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';

const SSOCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginWithToken } = useAuth();
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Completing your sign-in...');

  useEffect(() => {
    const handleSSOCallback = async () => {
      try {
        const token = searchParams.get('token');
        const error = searchParams.get('error');

        if (error) {
          let errorMessage = 'SSO authentication failed.';
          
          switch (error) {
            case 'sso_failed':
              errorMessage = 'SSO authentication failed. Please try again or contact your administrator.';
              break;
            case 'sso_no_user':
              errorMessage = 'Unable to create user account from SSO response. Please contact your administrator.';
              break;
            case 'sso_error':
              errorMessage = 'An error occurred during SSO processing. Please try again.';
              break;
            default:
              errorMessage = `SSO Error: ${error}`;
          }

          setStatus('error');
          setMessage(errorMessage);
          return;
        }

        if (!token) {
          setStatus('error');
          setMessage('No authentication token received. Please try signing in again.');
          return;
        }

        // Adopt the token and load the user behind it, so the account details
        // come from the server rather than from the token's own claims.
        const signedIn = await loginWithToken(token);

        if (!signedIn) {
          setStatus('error');
          setMessage('That sign-in token was not accepted. Please try signing in again.');
          return;
        }

        setStatus('success');
        setMessage('Successfully signed in! Redirecting...');
        setTimeout(() => navigate('/dashboard'), 1200);

      } catch (error) {
        console.error('SSO callback error:', error);
        setStatus('error');
        setMessage('An unexpected error occurred. Please try signing in again.');
      }
    };

    handleSSOCallback();
  }, [searchParams, navigate, loginWithToken]);

  const handleRetry = () => {
    navigate('/login');
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'processing':
        return <CircularProgress size={48} color="primary" />;
      case 'success':
        return <CheckCircle sx={{ fontSize: 48, color: 'success.main' }} />;
      case 'error':
        return <Error sx={{ fontSize: 48, color: 'error.main' }} />;
      default:
        return <CircularProgress size={48} color="primary" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return 'success.main';
      case 'error':
        return 'error.main';
      default:
        return 'text.primary';
    }
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      bgcolor="background.default"
      p={3}
    >
      <Paper 
        elevation={3}
        sx={{
          p: 4,
          maxWidth: 400,
          width: '100%',
          textAlign: 'center'
        }}
      >
        <Box mb={3}>
          {getStatusIcon()}
        </Box>

        <Typography 
          variant="h5" 
          component="h1" 
          gutterBottom
          color={getStatusColor()}
        >
          {status === 'processing' && 'Completing Sign-In'}
          {status === 'success' && 'Welcome!'}
          {status === 'error' && 'Sign-In Failed'}
        </Typography>

        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ mb: 3 }}
        >
          {message}
        </Typography>

        {status === 'error' && (
          <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
            <Typography variant="body2">
              If this problem persists, please contact your system administrator 
              or try signing in with your regular password.
            </Typography>
          </Alert>
        )}

        {status === 'error' && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleRetry}
            fullWidth
          >
            Try Again
          </Button>
        )}

        {status === 'success' && (
          <Typography variant="caption" color="text.secondary">
            Redirecting to dashboard...
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default SSOCallback;